import { ChangeEvent, useContext, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/store";
import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { cropSelector } from "store/features/crop/selectors/cropSelector";
import { CropState } from "store/features/crop/types";
import { cropRatioKeys, CropRatioType } from "store/types/crop";
import { loadCropper } from "client/utils/imageEdit/crop/loadCropper";
import { CropperOptions } from "client/utils/imageEdit/crop/cropTypes";
import {
    setCustomRatioX,
    setCustomRatioY,
    setHeight,
    setLeft,
    setRatio,
    setTop,
    setWidth,
    toggleCropOrientation as toggleCropOrientationAction,
} from "store/features/crop/actions";
import Cropper from "cropperjs";
import { getRatioValue } from "client/utils/getRatioValue";
import { useDebounceFunc } from "client/hooks/utils/useDebounceFunc";
import { useImageCropperAction } from "../../Action/useImageCropperAction";
import { divide } from "client/utils/divide";
import { useLoadFromParentImage } from "store/features/resizeEditor/hooks/useLoadFromParentImage";

let cropper: Cropper | null = null;
let isLoadedCache = false;

export const useImageCropperCropSettings = () => {
    const {
        crop: {
            width,
            height,
            ratio,
            customRatioValues: { x: customRatioX, y: customRatioY },
        },
        image,
    } = useAppSelector(cropSelector, equalityFn);
    const dispatch = useAppDispatch();
    const { cropRef, parentImageRef: canvasRef } = useContext(CanvasContext);
    const { isLoaded } = useLoadFromParentImage(cropRef);
    isLoadedCache = isLoaded;
    const resetButtonRef = useRef<HTMLDivElement>(null);

    const handleCrop = () => {
        return handleExport();
    };

    const handleReset = () => {
        if (!image) return;
        cropper && cropper.destroy();
        cropper = null;

        handleLoadCropper();
        handleRatioChange("freeform");

        document.body.dispatchEvent(new Event("crop-reset-complete"));
    };

    const handleToggleCropOrientation = () => {
        if (!image) return;

        const _height = height;
        const _width = width;

        dispatch(setHeight(_width));
        dispatch(setWidth(_height));

        cropper?.setCropBoxData({
            width: getScaledDownWidth(_height),
            height: getScaledDownHeight(_width),
        });

        if (width / height === image.originalWidth / image.originalHeight) {
            handleRatioChange("original");
        } else {
            const sameRatio = cropRatioKeys
                .filter((r) => r !== "custom" && r !== "original" && r !== "freeform")
                .find((r) => getRatioValue(r) === width / height);

            sameRatio ? handleRatioChange(sameRatio) : handleRatioChange("custom");
        }
    };

    const getScaledDownWidth = (width: number): number => {
        return image ? width / (image.originalWidth / image.initialWidth) : width;
    };

    const getScaledUpWidth = (width: number): number => {
        return image ? width * (image.originalWidth / image.initialWidth) : width;
    };

    const getScaledDownHeight = (height: number): number => {
        return image ? height / (image.originalHeight / image.initialHeight) : height;
    };

    const getScaledUpHeight = (height: number): number => {
        return image ? height * (image.originalHeight / image.initialHeight) : height;
    };

    const getScaledDownLeft = (left: number): number => {
        return image ? left / (image.originalWidth / image.initialWidth) : left;
    };

    const getScaledUpLeft = (left: number): number => {
        return image ? left * (image.originalWidth / image.initialWidth) : left;
    };

    const getScaledDownTop = (top: number): number => {
        return image ? top / (image.originalHeight / image.initialHeight) : top;
    };

    const getScaledUpTop = (top: number): number => {
        return image ? top * (image.originalHeight / image.initialHeight) : top;
    };

    const handleWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!image) return;
        const __width = Number(e.target.value);
        const _width = __width > 0 ? (__width > image.originalWidth ? image.originalWidth : __width) : 0;

        if (ratio !== "freeform") {
            let _height = height;
            if (ratio === "original") {
                _height = (image.originalHeight / image.originalWidth) * _width;
            } else if (ratio === "custom") {
                _height = divide(customRatioY, customRatioX) * _width;
            } else {
                _height = _width / getRatioValue(ratio);
            }
            dispatch(setHeight(_height));
        }

        dispatch(setWidth(_width));

        cropper?.setCropBoxData({
            width: getScaledDownWidth(_width),
        });
    };

    const handleHeightChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!image) return;
        const __height = Number(e.target.value);
        const _height = __height > 0 ? (__height > image.originalHeight ? image.originalHeight : __height) : 0;

        if (ratio !== "freeform") {
            let _width = width;
            if (ratio === "original") {
                _width = (image.originalWidth / image.originalHeight) * _height;
            } else if (ratio === "custom") {
                _width = divide(customRatioX, customRatioY) * _height;
            } else {
                _width = _height * getRatioValue(ratio);
            }

            dispatch(setWidth(_width));
        }

        dispatch(setHeight(_height));

        cropper?.setCropBoxData({
            height: getScaledDownHeight(_height),
        });
    };

    const handleRatioChange = (ratio: CropRatioType) => {
        if (!cropper || !image || !canvasRef?.current) return;

        const prevCropBox = cropper.getCropBoxData();

        if (ratio === "original") {
            cropper.setAspectRatio(image.ratio);
        } else if (ratio === "custom") {
            cropper?.setAspectRatio(1);
        } else {
            cropper.setAspectRatio(getRatioValue(ratio));
        }

        dispatch(
            setRatio({
                ratio,
                cropBoxData: cropper.getCropBoxData(),
            }),
        );

        const { width: newWidth, height: newHeight } = cropper.getCropBoxData();
        cropper.setCropBoxData({
            ...prevCropBox,
            width: newWidth,
            height: newHeight,
        });
        dispatch(setWidth(getScaledUpWidth(newWidth)));
        dispatch(setHeight(getScaledUpHeight(newHeight)));
    };

    const toggleCropOrientation = () => {
        if (!image || !cropper) return;

        dispatch(toggleCropOrientationAction());

        cropper.setCropBoxData({
            width: getScaledDownWidth(height),
            height: getScaledDownHeight(width),
        });

        if (width / height === image.originalWidth / image.originalHeight) {
            handleRatioChange("original");
        } else {
            const sameRatio = cropRatioKeys
                .filter((r) => r !== "custom" && r !== "original" && r !== "freeform")
                .find((r) => getRatioValue(r) === width / height);

            sameRatio ? handleRatioChange(sameRatio) : handleRatioChange("custom");
        }
    };

    const handleTopChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(setTop(+e.target.value));

        cropper?.setCropBoxData({
            top: getScaledDownTop(+e.target.value),
        });
    };

    const handleLeftChange = (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(setLeft(+e.target.value));

        cropper?.setCropBoxData({
            left: getScaledDownLeft(+e.target.value),
        });
    };

    const getOptions = (): CropperOptions => {
        return {
            aspectRatio: 0, // freeform
            zoomable: false,
            crop: (width, height, top, left) => {
                dispatch(setWidth(getScaledUpWidth(width)));
                dispatch(setHeight(getScaledUpHeight(height)));
                dispatch(setTop(getScaledUpTop(top)));
                dispatch(setLeft(getScaledUpLeft(left)));
            },
            cropend: (width, height) => {
                dispatch(setWidth(getScaledUpWidth(width)));
                dispatch(setHeight(getScaledUpHeight(height)));
            },
            cropdrag: (top, left) => {
                dispatch(setTop(getScaledUpTop(top)));
                dispatch(setLeft(getScaledUpLeft(left)));
            },
            cropresize: (width, height, top, left) => {
                dispatch(setWidth(getScaledUpWidth(width)));
                dispatch(setHeight(getScaledUpHeight(height)));
                dispatch(setTop(getScaledUpTop(top)));
                dispatch(setLeft(getScaledUpLeft(left)));
            },
            minContainerHeight: 0,
            minContainerWidth: 0,
        };
    };

    const handleLoadCropper = (): Cropper | null => {
        if (!isLoadedCache || !cropRef?.current || !image || cropper) return null;
        const { cropper: _cropper } = loadCropper(cropRef.current, getOptions());
        cropper = _cropper;
        return cropper;
    };

    const { handleClick: handleExport, isProcessing: isCropping, isActive } = useImageCropperAction();
    const cropDebounce = useDebounceFunc();
    const handleCropApplyEvent = () => {
        cropDebounce(async () => {
            await handleExport();
            document.body.dispatchEvent(new Event("crop-complete"));
        });
    };

    const handleCropResetEvent = () => {
        cropDebounce(() => {
            resetButtonRef.current?.click();
        });
    };

    const handleSelectAspectRatio = () => {
        handleRatioChange("1:1");
    };

    const handleUnSelectAspectRatio = () => {
        handleRatioChange("freeform");
    };

    const handleCustomRatioXChange = (value: number) => {
        dispatch(setCustomRatioX(value));
        handleApplyCustomRatio(value, customRatioY);
    };

    const handleCustomRatioYChange = (value: number) => {
        dispatch(setCustomRatioY(value));
        handleApplyCustomRatio(customRatioX, value);
    };

    const handleApplyCustomRatio = (x: number, y: number) => {
        if (x <= 0 || y <= 0 || !cropper) return;
        const prevCropBox = cropper.getCropBoxData();
        cropper.setAspectRatio(x / y);
        handleCropperRatioChangeSideEffect(cropper, prevCropBox);
    };

    const handleCropperRatioChangeSideEffect = (cropper: Cropper, prevCropBox: Cropper.CropBoxData) => {
        const { width: newWidth, height: newHeight } = cropper.getCropBoxData();
        cropper.setCropBoxData({
            ...prevCropBox,
            width: newWidth,
            height: newHeight,
        });
        dispatch(setWidth(getScaledUpWidth(newWidth)));
        dispatch(setHeight(getScaledUpHeight(newHeight)));
    };

    useEffect(() => {
        document.body.addEventListener("crop-apply", handleCropApplyEvent);
        document.body.addEventListener("crop-reset", handleCropResetEvent);

        return () => {
            document.body.removeEventListener("crop-apply", handleCropApplyEvent);
            document.body.removeEventListener("crop-reset", handleCropResetEvent);
        };
    }, []);

    useEffect(() => {
        if (cropper) {
            cropper.destroy();
            cropper = null;
        }
        if (isLoaded) {
            handleLoadCropper();
        }
    }, [isLoaded, image]);

    return {
        handleCrop,
        handleReset,
        handleToggleCropOrientation,
        handleWidthChange,
        handleHeightChange,
        handleRatioChange,
        toggleCropOrientation,
        handleTopChange,
        handleLeftChange,
        resetButtonRef,
        handleSelectAspectRatio,
        handleUnSelectAspectRatio,
        customRatioX,
        customRatioY,
        handleCustomRatioXChange,
        handleCustomRatioYChange,
        ratio,
        isCropping,
        isActive,
    };
};

const equalityFn = (left: CropState, right: CropState) => {
    if (
        left.crop.height !== right.crop.height ||
        left.crop.width !== right.crop.width ||
        left.crop.ratio !== right.crop.ratio ||
        JSON.stringify(left.image) !== JSON.stringify(right.image)
    )
        return false;

    return true;
};
